import { z } from 'zod';
/**
 * Spec — a live, mutable dependency graph (SpecSection nodes +
 * SpecSectionDependency edges) that renders into a single readable document
 * on demand. The graph is the source of truth; the rendered document is a
 * compiled view of it (the same relationship `.beads/issues.jsonl` has to a
 * beads database: a passive export, never the thing agents edit directly).
 * Sections can be atomically claimed by a persistent-agent thread — mirrors
 * beads' `bd update --claim`, deliberately orthogonal to section `status`
 * (content maturity), the same split beads makes between assignee and status.
 */
/** Also enforced at the DB + service layer. */
export const specDescriptionMax = 500;
export const specSectionTitleMax = 200;
export const specSectionBodyMax = 20_000;
/** kebab-case, unique per org. */
export const SpecNameSchema = z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Spec name must be kebab-case');
export const SpecSectionStatusSchema = z.enum(['DRAFT', 'PROPOSED', 'APPROVED', 'SUPERSEDED']);
/**
 * Beads-style issue type. TASK is the default/generic case; MISSION is meant
 * to be used with children via `parentSectionId`, mirroring beads'
 * hierarchical ids (bd-a3f8, bd-a3f8.1) without inventing a second id scheme.
 */
export const SpecSectionTypeSchema = z.enum(['TASK', 'MISSION']);
/**
 * Task lifecycle (mirrors beads' open/in_progress/closed/reopened) —
 * deliberately separate from `SpecSectionStatus` (content maturity).
 * `taskStatus`, NOT `status`, is what gates BLOCKS readiness: a CLOSED
 * section unblocks its dependents, same as beads treating a closed issue as
 * unblocking.
 */
export const SpecSectionTaskStatusSchema = z.enum(['OPEN', 'IN_PROGRESS', 'CLOSED', 'REOPENED']);
/** Settable via `spec_update_section` — CLOSED is deliberately excluded; go through spec_close_section instead so `closedAt`/`closeComment` stay consistent. */
export const SpecSectionUpdatableTaskStatusSchema = z.enum(['OPEN', 'IN_PROGRESS', 'REOPENED']);
/**
 * Only BLOCKS affects readiness/render-ordering (mirrors beads' dependency
 * vocabulary); RELATES_TO/DISCOVERED_FROM/DUPLICATES are non-blocking links
 * surfaced in the rendered doc, and SUPERSEDES excludes the superseded
 * section's body from the render.
 */
export const SpecDependencyTypeSchema = z.enum([
    'BLOCKS',
    'RELATES_TO',
    'SUPERSEDES',
    'DISCOVERED_FROM',
    'DUPLICATES',
]);
// ─── Spec (entity) ──────────────────────────────────────────────────────────
export const SpecSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    name: SpecNameSchema,
    description: z.string().max(specDescriptionMax).nullable(),
    createdBySubject: z.string().nullable(),
    deletedAt: z.date().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
// ─── SpecSection (graph node) ───────────────────────────────────────────────
/**
 * Optional physical-world anchor for a section — deliberately unrelated to
 * `position` (document/render ordering, an integer, not a place). A
 * discriminated union rather than one flat shape because "where" means
 * different things depending on the robot's operating environment:
 *
 * - `gps`: outdoor, global — a WGS84 fix. `heading` (compass degrees, 0-360,
 *   0=north) is optional since not every fix carries orientation.
 * - `2d`: indoor/SLAM — a point on a specific floorplan/occupancy map.
 *   `mapId` is required: a bare (x, y) is meaningless without knowing which
 *   map it's relative to. `heading` (degrees) mirrors ROS's `Pose2D` (x, y,
 *   theta) convention.
 * - `3d`: a point in a specific 3D site/field frame (e.g. outdoor mapped
 *   terrain, a multi-level facility). `mapId` anchors it the same way as
 *   `2d`. No orientation here — a full 3D attitude is a quaternion/euler
 *   can of worms not worth opening until something actually needs it.
 */
export const SpecSectionGpsLocationSchema = z.object({
    type: z.literal('gps'),
    lat: z
        .number()
        .min(-90)
        .max(90)
        .describe('WGS84 latitude — a real-world GPS fix, not a UI coordinate'),
    lng: z.number().min(-180).max(180).describe('WGS84 longitude'),
    altitude: z.number().optional().describe('Meters above sea level, optional'),
    heading: z
        .number()
        .min(0)
        .max(360)
        .optional()
        .describe('Compass heading in degrees, 0 = north, optional'),
});
export const SpecSection2dLocationSchema = z.object({
    type: z.literal('2d'),
    mapId: z
        .string()
        .min(1)
        .describe('Which physical indoor/SLAM floorplan this x/y is relative to — required, a bare coordinate is meaningless without it'),
    x: z.number().describe('Real-world position in the mapId frame, not a UI/document coordinate'),
    y: z.number().describe('Real-world position in the mapId frame, not a UI/document coordinate'),
    heading: z
        .number()
        .min(0)
        .max(360)
        .optional()
        .describe('Degrees, mirrors ROS Pose2D theta, optional'),
});
export const SpecSection3dLocationSchema = z.object({
    type: z.literal('3d'),
    mapId: z
        .string()
        .min(1)
        .describe('Which physical 3D site/field frame this x/y/z is relative to — required, a bare coordinate is meaningless without it'),
    x: z.number().describe('Real-world position in the mapId frame, not a UI/document coordinate'),
    y: z.number().describe('Real-world position in the mapId frame, not a UI/document coordinate'),
    z: z.number().describe('Real-world position in the mapId frame, not a UI/document coordinate'),
});
export const SpecSectionLocationSchema = z
    .discriminatedUnion('type', [
    SpecSectionGpsLocationSchema,
    SpecSection2dLocationSchema,
    SpecSection3dLocationSchema,
])
    .describe('A physical-world anchor for this section (where the work actually happens in the real ' +
    'world), deliberately unrelated to `position` (document ordering, not a place). Not a UI ' +
    'or graph-layout coordinate.');
export const SpecSectionSchema = z.object({
    id: z.string(),
    specId: z.string(),
    orgId: z.string(),
    title: z.string().max(specSectionTitleMax),
    body: z.string().max(specSectionBodyMax),
    position: z.number().int(),
    status: SpecSectionStatusSchema,
    /** 0 = P0 (highest) .. 3 = P3 (lowest), mirrors `bd create -p 0`. */
    priority: z.number().int().min(0).max(3),
    sectionType: SpecSectionTypeSchema,
    taskStatus: SpecSectionTaskStatusSchema,
    /** Mission/subtask nesting (mirrors beads' bd-a3f8 / bd-a3f8.1 hierarchy) as a real FK — not spec-scoped, same as dependency edges. */
    parentSectionId: z.string().nullable(),
    closedAt: z.date().nullable(),
    closeComment: z.string().nullable(),
    location: SpecSectionLocationSchema.nullable(),
    /** Estimated time to complete, in seconds — optional, no shape to validate (unlike `location`). */
    estimatedSeconds: z.number().int().positive().nullable(),
    /** The persistent-agent thread that currently owns (claimed) this section. */
    ownerAgentId: z.string().nullable(),
    claimedAt: z.date().nullable(),
    createdBySubject: z.string().nullable(),
    deletedAt: z.date().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
// ─── SpecSectionDependency (graph edge) ─────────────────────────────────────
/**
 * Deliberately NOT spec-scoped — a section in one spec may BLOCKS/RELATES_TO/
 * etc. a section in another (beads has one flat issue graph, not one graph
 * per project). Both endpoints must still belong to the same org.
 */
export const SpecSectionDependencySchema = z.object({
    id: z.string(),
    orgId: z.string(),
    fromSectionId: z.string(),
    toSectionId: z.string(),
    depType: SpecDependencyTypeSchema,
    createdAt: z.date(),
});
// ─── Binding (spec ↔ agent, many-to-many) ──────────────────────────────────
export const SpecBindingSchema = z.object({
    specId: z.string(),
    agentId: z.string(),
    orgId: z.string(),
});
/** API read shape — a spec with its full graph and bound agents. */
export const SpecWithSectionsSchema = SpecSchema.extend({
    agentIds: z.array(z.string()),
    sections: z.array(SpecSectionSchema),
    dependencies: z.array(SpecSectionDependencySchema),
});
/** `bd blocked`-shaped: a section and the (unresolved BLOCKS) sections holding it back. */
export const SpecBlockedSectionSchema = z.object({
    section: SpecSectionSchema,
    blockedBy: z.array(SpecSectionSchema),
});
// ─── Create / Update ────────────────────────────────────────────────────────
export const CreateSpecInputSchema = z.object({
    name: SpecNameSchema,
    description: z.string().max(specDescriptionMax).optional(),
    /** Optional initial bindings — a spec may also be created unbound and bound later. */
    agentIds: z.array(z.string()).optional().default([]),
});
/** Entity-level edit (description) — distinct from section content edits. */
export const UpdateSpecInputSchema = z.object({
    description: z.string().max(specDescriptionMax).optional(),
});
/** Rebind which agents can see/edit this spec. Never cuts a version. */
export const UpdateSpecBindingsInputSchema = z.object({
    agentIds: z.array(z.string()),
});
export const AddSpecSectionInputSchema = z
    .object({
    /** Optional — the REST endpoint takes specId from the URL path; only the MCP tool layer needs it in the body. */
    specId: z.string().optional(),
    title: z.string().min(1).max(specSectionTitleMax),
    body: z.string().min(1).max(specSectionBodyMax),
    position: z.number().int().optional(),
    /** Insert immediately after/before this sibling section (mutually exclusive). */
    after: z.string().optional(),
    before: z.string().optional(),
    priority: z.number().int().min(0).max(3).optional().describe('Default 2 (P2)'),
    sectionType: SpecSectionTypeSchema.optional().describe('Default TASK'),
    taskStatus: SpecSectionTaskStatusSchema.optional().describe('Default OPEN'),
    parentSectionId: z.string().optional().describe('Nest under a mission/parent section'),
    location: SpecSectionLocationSchema.optional().describe('Optional physical anchor: gps (lat/lng/altitude?/heading?), 2d (mapId/x/y/heading?), or 3d (mapId/x/y/z)'),
    estimatedSeconds: z
        .number()
        .int()
        .positive()
        .optional()
        .describe('Optional estimated time to complete, in seconds'),
})
    .superRefine((v, ctx) => {
    if (v.after && v.before) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Specify at most one of after/before',
            path: ['before'],
        });
    }
});
export const UpdateSpecSectionInputSchema = z.object({
    title: z.string().min(1).max(specSectionTitleMax).optional(),
    body: z.string().min(1).max(specSectionBodyMax).optional(),
    status: SpecSectionStatusSchema.optional(),
    priority: z.number().int().min(0).max(3).optional(),
    sectionType: SpecSectionTypeSchema.optional(),
    /** Excludes CLOSED — use spec_close_section so closedAt/closeComment stay consistent. */
    taskStatus: SpecSectionUpdatableTaskStatusSchema.optional(),
    /** Null detaches from its current parent. */
    parentSectionId: z.string().nullable().optional(),
    location: SpecSectionLocationSchema.nullable()
        .optional()
        .describe('Physical-world anchor (gps/2d/3d) — see spec_add_section. Null clears it.'),
    estimatedSeconds: z
        .number()
        .int()
        .positive()
        .nullable()
        .optional()
        .describe('Estimated time to complete, in seconds. Null clears a previously-set estimate.'),
});
/** Sets taskStatus=CLOSED + closedAt + an optional resolution comment (mirrors `bd close <id> "reason"`). */
export const CloseSpecSectionInputSchema = z.object({
    comment: z.string().min(1).optional(),
});
/** Sets taskStatus=REOPENED. closedAt/closeComment are left as history of the last close. */
export const ReopenSpecSectionInputSchema = z.object({});
export const AddSpecSectionDependencyInputSchema = z
    .object({
    /** Optional — the REST endpoint takes fromSectionId from the URL path; only the MCP tool layer needs it in the body. */
    fromSectionId: z.string().optional(),
    toSectionId: z.string(),
    depType: SpecDependencyTypeSchema.optional().default('BLOCKS'),
})
    .superRefine((v, ctx) => {
    if (v.fromSectionId !== undefined && v.fromSectionId === v.toSectionId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'A section cannot depend on itself',
            path: ['toSectionId'],
        });
    }
});
/**
 * Atomic claim (CAS: fails unless ownerAgentId is currently unset). At the
 * MCP-tool layer `ownerAgentId` is filled in from the calling thread's
 * ambient context (closure), never an LLM-controlled parameter — mirrors
 * how `save_note`/`set_status` bind actor identity at tool-build time.
 */
export const ClaimSpecSectionInputSchema = z.object({
    ownerAgentId: z.string(),
});
/** Explicit assignment/release. `ownerAgentId: null` releases the section. */
export const AssignSpecSectionInputSchema = z.object({
    ownerAgentId: z.string().nullable(),
});
/**
 * Multi-op graph edit in one request. Prefer this over N× `addSection` /
 * `updateSection` / `addDependency` when drafting or restructuring a whole
 * area of the spec — one round-trip, one transaction, tempIds so newly-added
 * sections can be referenced by deps/placement in the same patch.
 *
 * `tempId` is a client-chosen token (e.g. "sec_a"); it is never persisted.
 * `after`/`before`/`fromSectionId`/`toSectionId` may be either a real section
 * id or a `tempId` from an earlier entry in `addSections` (order matters for
 * placement refs).
 */
export const ApplySpecPatchInputSchema = z
    .object({
    addSections: z
        .array(z.object({
        tempId: z
            .string()
            .min(1)
            .max(64)
            .optional()
            .describe('Client token so deps/placement in this patch can refer to the new section'),
        title: z.string().min(1).max(specSectionTitleMax),
        body: z.string().min(1).max(specSectionBodyMax),
        position: z.number().int().optional(),
        after: z.string().optional(),
        before: z.string().optional(),
        status: SpecSectionStatusSchema.optional(),
        priority: z.number().int().min(0).max(3).optional(),
        sectionType: SpecSectionTypeSchema.optional(),
        taskStatus: SpecSectionTaskStatusSchema.optional(),
        /** May be a real section id or an earlier entry's tempId. */
        parentSectionId: z.string().optional(),
        location: SpecSectionLocationSchema.optional().describe('Physical-world anchor: gps (lat/lng/altitude?/heading?), 2d (mapId/x/y/heading?), or 3d (mapId/x/y/z)'),
        estimatedSeconds: z
            .number()
            .int()
            .positive()
            .optional()
            .describe('Optional estimated time to complete, in seconds'),
    }))
        .max(50)
        .optional(),
    updateSections: z
        .array(z.object({
        sectionId: z.string().min(1),
        title: z.string().min(1).max(specSectionTitleMax).optional(),
        body: z.string().min(1).max(specSectionBodyMax).optional(),
        status: SpecSectionStatusSchema.optional(),
        priority: z.number().int().min(0).max(3).optional(),
        sectionType: SpecSectionTypeSchema.optional(),
        taskStatus: SpecSectionUpdatableTaskStatusSchema.optional(),
        parentSectionId: z.string().nullable().optional(),
        location: SpecSectionLocationSchema.nullable()
            .optional()
            .describe('Physical-world anchor (gps/2d/3d) — see spec_add_section. Null clears it.'),
        estimatedSeconds: z
            .number()
            .int()
            .positive()
            .nullable()
            .optional()
            .describe('Estimated time to complete, in seconds. Null clears a previously-set estimate.'),
    }))
        .max(50)
        .optional(),
    addDependencies: z
        .array(z.object({
        fromSectionId: z.string().min(1),
        toSectionId: z.string().min(1),
        depType: SpecDependencyTypeSchema.optional().default('BLOCKS'),
    }))
        .max(100)
        .optional(),
})
    .superRefine((v, ctx) => {
    const adds = v.addSections?.length ?? 0;
    const updates = v.updateSections?.length ?? 0;
    const deps = v.addDependencies?.length ?? 0;
    if (adds + updates + deps === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Patch must include at least one of addSections, updateSections, addDependencies',
        });
    }
    const temps = new Set();
    for (const s of v.addSections ?? []) {
        if (!s.tempId)
            continue;
        if (temps.has(s.tempId)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Duplicate tempId "${s.tempId}" in addSections`,
                path: ['addSections'],
            });
        }
        temps.add(s.tempId);
        if (s.after && s.before) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Specify at most one of after/before',
                path: ['addSections'],
            });
        }
    }
});
//# sourceMappingURL=spec.js.map